import { faCamera } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState, useRef, useContext, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import LoadingSpinner from '../../../shared/components/UIElements/LoadingSpinner';
import { AuthContext } from '../../../shared/context/auth-context';
import { useHttpClient } from '../../../shared/hooks/http-hook';
import WhitePopUpNoTitle from '../../../shared/modal/whitePopUp/WhitePopUpNoTitle';
import { rewriteS3Url } from '../../../shared/util/s3Helper';

type ImageUploadBtnProps = {
  saveProfileImage: (url: string) => void;
};

export function ImageUploadBtn({ saveProfileImage }: ImageUploadBtnProps) {
  const auth = useContext(AuthContext);
  const { t } = useTranslation();
  const { isLoading, sendRequest } = useHttpClient();
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [modalTextContent, setModalTextContent] = useState({});
  const fileInputRef = useRef(null);

  const modalClickHandler = useCallback(() => {
    setModalIsOpen(false);
  }, [modalIsOpen]);

  const openModal = useCallback(
    (title, text) => {
      setModalIsOpen(true);
      setModalTextContent({ title, text });
    },
    [modalIsOpen, modalTextContent]
  );

  const uploadImage = async (file) => {
    try {
      const fileName = auth.userId;
      const renamedFile = new File([file], fileName);

      // Get Signed URL for AWS S3 upload
      const presignedUrl = await sendRequest(
        `${process.env.REACT_APP_BACKEND_URL}/users/getSignedUrl/${
          auth.userId
        }?filename=${encodeURIComponent(fileName)}`,
        'PUT'
      );

      if (presignedUrl) {
        // Upload image to AWS S3
        await sendRequest(presignedUrl.toString(), 'PUT', renamedFile, {
          'Content-Type': 'image/jpeg',
        });

        const url = rewriteS3Url(presignedUrl.split('?')[0]);
        // Save profile image
        saveProfileImage(url);
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      openModal('Profile', ['An error occurred while uploading the image. Please try again.']);
    }
  };

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];

      // FileReader to validate image header
      const fileReader = new FileReader();
      fileReader.onloadend = (e) => {
        // Validate image header
        const arr = new Uint8Array(e.target.result as ArrayBuffer).subarray(0, 4);
        let header = '';
        for (let i = 0; i < arr.length; i++) {
          header += arr[i].toString(16);
        }

        // Check if the header matches valid image formats
        if (
          ![
            '89504e47',
            'ffd8ffee',
            'ffd8ffe0',
            'ffd8ffe1',
            'ffd8ffe2',
            'ffd8ffe3',
            'ffd8ffe8',
            'ffd8ffdb',
          ].includes(header)
        ) {
          openModal('Profile', ['Please upload a valid image (PNG or JPEG)']);
          return; // Stop further processing
        }

        // Validate file size
        const fsize = Math.round(file.size / 1024);
        if (fsize >= 4096) {
          openModal('Profile', ['Your file is too large, please select a file less than 4MB']);
          return;
        }
        if (fsize < 21) {
          openModal('Profile', ['Your file is too small, please select a file larger than 20KB']);
          return;
        }

        // If all validations pass
        uploadImage(file);
      };

      // Read the first bytes of the file to trigger onloadend
      fileReader.readAsArrayBuffer(file.slice(0, 4));
    }
  };

  return (
    <>
      {isLoading && <LoadingSpinner asOverlay />}
      {modalIsOpen && (
        <WhitePopUpNoTitle
          returnFocus
          popupContents={modalTextContent}
          ctaBtn={t('T015')}
          handleSubmit={modalClickHandler}
          dialogTitle={t('modal.imageError.explanation')}
        />
      )}
      <label htmlFor="file-upload" className="visuallyHidden">
        {t('SD005')}
      </label>
      <input
        id="file-upload"
        type="file"
        accept=".jpg, .png, .jpeg"
        onChange={handleImageChange}
        style={{ display: 'none' }}
        ref={fileInputRef}
      />
      <button
        type="button"
        className="photo-edit"
        aria-label={t('SD029')}
        onClick={() => fileInputRef.current?.click()}
      >
        <span className="visuallyHidden">{t('SD029')}</span>
        <FontAwesomeIcon icon={faCamera} className="camera" />
      </button>
    </>
  );
}
